// Mirrors backend/server.py's validate_person_name(), validate_message_text(), and
// PHONE_RE — keep all three in sync.

const NAME_VOWELS = new Set("aeiouàáâãäåèéêëìíîïòóôõöùúûüāēīōūæœø".split(""));
// Longest consonant run in a real name we could think of (Polish "Krzysztof" runs 7) plus a
// safety margin — high enough to leave legitimate international names alone, low enough to
// still catch keyboard-mash strings like "zhdgjjyhjyhjy", which run the full length.
const MAX_CONSONANT_RUN = 8;

// Returns an error message string, or null if the name looks valid.
export function validatePersonName(raw) {
  const v = (raw || "").trim();
  if (v.length < 2) return "Name must be at least 2 characters.";
  const lettersOnly = v.replace(/[ '\-.]/g, "");
  if (!lettersOnly || !/^\p{L}+$/u.test(lettersOnly)) {
    return "Name may only contain letters, spaces, hyphens, and apostrophes.";
  }
  let run = 0;
  for (const ch of lettersOnly.toLowerCase()) {
    run = NAME_VOWELS.has(ch) ? 0 : run + 1;
    if (run >= MAX_CONSONANT_RUN) return "Please enter a valid name.";
  }
  return null;
}

export const PHONE_PATTERN = /^[0-9+()\-.\s]{6,30}$/;

// Catches the same auto-generated-junk pattern as validatePersonName, but scoped to
// single-token messages only (real messages — even short ones — have spaces; a bot filling
// every field with one random string doesn't). A multi-word message is never touched here,
// so this can't misfire on legitimate free text. Returns an error message, or null if OK.
export function validateMessageText(raw) {
  const v = (raw || "").trim();
  if (!v || /\s/.test(v)) return null;
  const core = v.replace(/[.,!?;:'"()\-]/g, "");
  // A single character repeated (e.g. "rrrrr", "aaaa") is never a real message no matter
  // how short — checked before the length gate below, which exists only to leave short
  // *varied* strings like "N/A" or "-" alone.
  if (core.length >= 4 && new Set(core.toLowerCase()).size === 1) return "Please enter a real message.";
  if (v.length < 8) return null;
  const hasDigit = /\d/.test(core);
  const hasAlpha = /\p{L}/u.test(core);
  if (hasDigit && hasAlpha) return "Please enter a real message.";
  if (hasAlpha && /^\p{L}+$/u.test(core)) {
    let run = 0;
    for (const ch of core.toLowerCase()) {
      run = NAME_VOWELS.has(ch) ? 0 : run + 1;
      if (run >= MAX_CONSONANT_RUN) return "Please enter a real message.";
    }
  }
  return null;
}
